import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Plus, Search, Tag, Trash2, X } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from './config/api';
import { useToast } from './shared/Toast';
import ConfirmModal from './shared/ConfirmModal';
import { productDisplayPrice } from '../utils/pricing';
import { effectiveFixedDiscountPercent } from '../utils/adminPriceDisplay';
import { formatMoney } from '../utils/formatMoney';
import { LoadFailed, SwitchRow } from './settings/instantSettings';
import { SheetField } from './settings/sheet';
import {
  ASIDE_BTN, Column, DANGER_GHOST_BTN, DataTable, DetailPanel, EmptyState, GHOST_BTN, INPUT_CLS, ListPage, ListToolbar,
  Notice, PRIMARY_BTN, matchesSearch,
} from './shared/listPage';

/**
 * «Προσφορές» (Κατάλογος) στη διάταξη «Πίνακας» (14/10/2026): λίστα + συρτάρι με τη φόρμα, ίδιοι κανόνες
 * επικύρωσης και ίδια σώματα με πριν:
 *   GET    /api/stores/:id/promotions             → { promotions }
 *   GET    /api/products/stores/:id/products      (για την επιλογή προϊόντων· τιμή take away)
 *   POST   /api/stores/:id/promotions             νέα            — σώμα: payloadOf(form)
 *   PUT    /api/stores/:id/promotions/:pid        αλλαγή         — σώμα: payloadOf(form)
 *   PUT    /api/stores/:id/promotions/:pid        «Ενεργή» γραμμής — σώμα: { ...promo, is_active: !promo.is_active }
 *   DELETE /api/stores/:id/promotions/:pid
 * Ως 14/10 η φόρμα άνοιγε στη θέση της λίστας και τα κείμενα ήταν γραμμένα στον κώδικα· τώρα συρτάρι, i18n.
 */

type PromotionType = 'percentage' | 'fixed_amount' | 'buy_x_get_y' | 'bundle_price' | 'order_fixed_amount';

interface Promotion {
  id?: number;
  store_id?: number;
  name: string;
  description?: string;
  promotion_type: PromotionType;
  discount_value?: number | null;
  bundle_price?: number | null;
  buy_quantity?: number | null;
  get_quantity?: number | null;
  min_order_amount?: number;
  starts_at?: string | null;
  ends_at?: string | null;
  days_of_week?: number[] | null;
  time_from?: string | null;
  time_to?: string | null;
  is_active: boolean;
  product_ids: number[];
}

interface ProductLite {
  id: number;
  name: string;
  // Τιμή εμφάνισης take-away (productDisplayPrice: η τιμή του προεπιλεγμένου μεγέθους).
  price: number;
  category_name?: string;
  max_discount_percent?: number;
}

// Η σειρά των τύπων στη φόρμα (ίδια με πριν).
const PROMOTION_TYPES: PromotionType[] = ['percentage', 'fixed_amount', 'buy_x_get_y', 'bundle_price', 'order_fixed_amount'];

// Θέση = getDay() της JS (0 = Κυριακή), όπως αποθηκεύεται στο days_of_week.
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

const emptyPromo: Promotion = {
  name: '',
  description: '',
  promotion_type: 'percentage',
  discount_value: 10,
  bundle_price: null,
  buy_quantity: null,
  get_quantity: null,
  min_order_amount: 0,
  starts_at: null,
  ends_at: null,
  days_of_week: null,
  time_from: null,
  time_to: null,
  is_active: true,
  product_ids: []
};

type LoadState = 'ready' | 'error' | 'forbidden';
/** Ποιο πεδίο δεν περνά την επικύρωση (για focus και aria-invalid). */
type ErrorField = 'name' | 'discount' | 'quantities' | 'bundle' | 'products';
type FormError = { field: ErrorField; message: string };

/** 'YYYY-MM-DD…' → «14/10/2026» χωρίς μετατροπή ζώνης (είναι ημερομηνία ημερολογίου, όχι στιγμή). */
const fmtYmd = (value?: string | null): string => {
  const s = String(value || '').slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
};

const LEGEND_CLS = 'text-sm font-medium text-t2 mb-2';
const SUB_LABEL_CLS = 'block text-xs text-t3 mb-1';

/** Επιλογή-«κουτί» με εγγενές radio/checkbox (όπως στη φόρμα εκτυπωτή): βελάκια, Space, αναγνώστες οθόνης. */
const Choice: React.FC<{
  type: 'radio' | 'checkbox';
  name?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  testId?: string;
}> = ({ type, name, checked, onChange, label, testId }) => (
  <label className="relative inline-flex" data-testid={testId}>
    <input type={type} name={name} className="peer sr-only" checked={checked} onChange={e => onChange(e.target.checked)} />
    <span
      className={`inline-flex items-center gap-1.5 min-h-[40px] px-3.5 rounded-xl border text-sm font-medium transition-colors cursor-pointer
        peer-focus-visible:ring-2 peer-focus-visible:ring-brand peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface-1
        ${checked ? 'border-brand bg-brand-soft text-brand font-semibold' : 'border-line-strong bg-surface-2 text-t2 hover:bg-surface-3'}`}
    >
      {type === 'checkbox' && checked && <Check className="w-3.5 h-3.5" aria-hidden="true" />}
      {label}
    </span>
  </label>
);

const PromotionsManagement: React.FC = () => {
  const { t } = useTranslation(['storePromotions', 'settings', 'common', 'listPage']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const uid = useId();
  const formId = `${uid}-form`;

  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [products, setProducts] = useState<ProductLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadState, setLoadState] = useState<LoadState>('ready');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<Promotion>(emptyPromo);
  // Η φόρμα όπως άνοιξε: με αλλαγές, το κλείσιμο ρωτά πρώτα.
  const [initialForm, setInitialForm] = useState<Promotion>(emptyPromo);
  const [productPickerOpen, setProductPickerOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; id: number | null }>({ open: false, id: null });
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [formError, setFormError] = useState<FormError | null>(null);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const nameRef = useRef<HTMLInputElement>(null);
  const discountRef = useRef<HTMLInputElement>(null);
  const buyRef = useRef<HTMLInputElement>(null);
  const bundleRef = useRef<HTMLInputElement>(null);
  const pickerRef = useRef<HTMLButtonElement>(null);

  const headers = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
  });

  // initial: μόνο η πρώτη φόρτωση δείχνει σφάλμα στη θέση της λίστας· μια ανανέωση μετά από αποθήκευση που
  // αποτυγχάνει κρατά την τελευταία λίστα (όπως πριν).
  const fetchPromotions = async (initial = false) => {
    if (!store?.id) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/stores/${store.id}/promotions`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setPromotions(data.promotions || []);
        setLoadState('ready');
      } else if (initial) {
        setLoadState(res.status === 403 ? 'forbidden' : 'error');
      }
    } catch (e) {
      console.error('Failed to fetch promotions', e);
      if (initial) setLoadState('error');
    }
  };

  const fetchProducts = async () => {
    if (!store?.id) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/products/stores/${store.id}/products`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        const list: ProductLite[] = (Array.isArray(data) ? data : []).map((p: any) => ({
          id: p.id,
          name: p.name,
          price: productDisplayPrice(p, 'take_away'),
          category_name: p.category_name,
          max_discount_percent: p.max_discount_percent !== undefined && p.max_discount_percent !== null
            ? Number(p.max_discount_percent)
            : 100
        }));
        setProducts(list);
      }
    } catch (e) {
      console.error('Failed to fetch products', e);
    }
  };

  const loadAll = () => {
    setLoading(true);
    Promise.all([fetchPromotions(true), fetchProducts()]).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!store?.id) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const openForm = (next: Promotion, id: number | null) => {
    setForm(next);
    setInitialForm(next);
    setEditingId(id);
    setFormError(null);
    setProductPickerOpen(false);
    setProductSearch('');
    setShowForm(true);
  };

  const startCreate = () => {
    openForm({ ...emptyPromo }, null);
  };

  const startEdit = (promo: Promotion) => {
    openForm({
      ...emptyPromo,
      ...promo,
      days_of_week: promo.days_of_week || null,
      starts_at: promo.starts_at ? String(promo.starts_at).slice(0, 10) : null,
      ends_at: promo.ends_at ? String(promo.ends_at).slice(0, 10) : null,
      time_from: promo.time_from ? String(promo.time_from).slice(0, 5) : null,
      time_to: promo.time_to ? String(promo.time_to).slice(0, 5) : null,
      product_ids: promo.product_ids || []
    }, promo.id || null);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyPromo);
    setInitialForm(emptyPromo);
    setFormError(null);
    setConfirmDiscard(false);
  };

  const dirty = showForm && JSON.stringify(form) !== JSON.stringify(initialForm);

  // Esc / X / φόντο / «Άκυρο»: πρώτα ένα ανοιχτό παράθυρο επιβεβαίωσης· με αλλαγές, ερώτηση.
  const requestClose = () => {
    if (confirmDelete.open) {
      setConfirmDelete({ open: false, id: null });
      return;
    }
    if (confirmDiscard) {
      setConfirmDiscard(false);
      return;
    }
    if (saving) return;
    if (dirty) setConfirmDiscard(true);
    else cancelForm();
  };

  /** Κάθε αλλαγή στη φόρμα σβήνει το μήνυμα επικύρωσης (θα ξαναελεγχθεί στην αποθήκευση). */
  const update = (patch: Partial<Promotion>) => {
    setFormError(null);
    setForm(prev => ({ ...prev, ...patch }));
  };

  // Validation: warn if any selected product's max_discount_percent < effective discount of this promotion
  const conflictingProducts = useMemo(() => {
    const selectedProducts = products.filter(p => form.product_ids.includes(p.id));
    if (selectedProducts.length === 0) return [];

    let effectivePct: number | null = null;
    if (form.promotion_type === 'percentage' && form.discount_value) {
      effectivePct = Number(form.discount_value);
    } else if (form.promotion_type === 'buy_x_get_y' && form.buy_quantity && form.get_quantity) {
      const buy = Number(form.buy_quantity);
      const get = Number(form.get_quantity);
      effectivePct = (get / (buy + get)) * 100;
    }
    // For fixed_amount and bundle_price the effective % depends on each product price/qty,
    // so we approximate per product below.

    if (effectivePct === null && form.promotion_type === 'fixed_amount' && form.discount_value) {
      const amount = Number(form.discount_value);
      return selectedProducts
        .map(p => ({ p, eff: effectiveFixedDiscountPercent(p.price, amount) }))
        // eff null = τιμή 0/άγνωστη: δεν ξέρουμε το %, οπότε καμία (ψεύτικη) προειδοποίηση.
        .filter(({ p, eff }) => eff !== null && eff > (p.max_discount_percent ?? 100))
        .map(({ p, eff }) => ({ ...p, effective_percent: eff as number }));
    }

    // Το κουπόνι αξίας παρακάμπτει σκόπιμα τα όρια περιθωρίου: η αξία δίνεται
    // συνειδητά από την καμπάνια (ίδια απόφαση και στο backend).
    if (form.promotion_type === 'order_fixed_amount') return [];
    if (effectivePct === null) return []; // bundle_price — effective % depends on basket combo, skip the check here

    return selectedProducts
      .filter(p => effectivePct! > (p.max_discount_percent ?? 100))
      .map(p => ({ ...p, effective_percent: effectivePct! }));
  }, [form, products]);

  // Οι ίδιοι κανόνες, με την ίδια σειρά, με πριν· το μήνυμα πάει δίπλα στο κουμπί και το focus στο πεδίο.
  const validate = (): FormError | null => {
    if (!form.name.trim()) return { field: 'name', message: t('storePromotions:validation.name') };
    if (form.promotion_type === 'percentage') {
      const v = Number(form.discount_value);
      if (!(v > 0 && v <= 100)) return { field: 'discount', message: t('storePromotions:validation.percentage') };
    }
    if (form.promotion_type === 'fixed_amount') {
      const v = Number(form.discount_value);
      if (!(v > 0)) return { field: 'discount', message: t('storePromotions:validation.fixedAmount') };
    }
    if (form.promotion_type === 'buy_x_get_y') {
      if (!form.buy_quantity || !form.get_quantity) return { field: 'quantities', message: t('storePromotions:validation.quantities') };
    }
    if (form.promotion_type === 'bundle_price') {
      if (form.bundle_price === null || form.bundle_price === undefined || Number(form.bundle_price) < 0)
        return { field: 'bundle', message: t('storePromotions:validation.bundle') };
    }
    if (form.promotion_type === 'order_fixed_amount') {
      const v = Number(form.discount_value);
      if (!(v > 0)) return { field: 'discount', message: t('storePromotions:validation.coupon') };
    }
    // Το κουπόνι αξίας ισχύει σε ΟΛΗ την παραγγελία όταν δεν επιλεγεί προϊόν —
    // η επιλογή προϊόντων είναι προαιρετικός περιορισμός («3€ στους καφέδες»).
    if (form.promotion_type !== 'order_fixed_amount' && form.product_ids.length === 0) {
      return { field: 'products', message: t('storePromotions:validation.products') };
    }
    return null;
  };

  const focusError = (field: ErrorField) => {
    const target = {
      name: nameRef, discount: discountRef, quantities: buyRef, bundle: bundleRef, products: pickerRef,
    }[field].current;
    target?.focus();
  };

  const save = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!store?.id || saving) return;
    const err = validate();
    if (err) {
      setFormError(err);
      focusError(err.field);
      return;
    }
    const payload: any = {
      name: form.name.trim(),
      description: form.description || null,
      promotion_type: form.promotion_type,
      discount_value: ['percentage', 'fixed_amount', 'order_fixed_amount'].includes(form.promotion_type) ? Number(form.discount_value) : null,
      bundle_price: form.promotion_type === 'bundle_price' ? Number(form.bundle_price) : null,
      buy_quantity: form.promotion_type === 'buy_x_get_y' ? Number(form.buy_quantity) : null,
      get_quantity: form.promotion_type === 'buy_x_get_y' ? Number(form.get_quantity) : null,
      min_order_amount: Number(form.min_order_amount) || 0,
      starts_at: form.starts_at || null,
      ends_at: form.ends_at || null,
      days_of_week: Array.isArray(form.days_of_week) && form.days_of_week.length > 0 ? form.days_of_week : null,
      time_from: form.time_from || null,
      time_to: form.time_to || null,
      is_active: !!form.is_active,
      product_ids: form.product_ids
    };
    setSaving(true);
    try {
      const url = editingId
        ? `${API_BASE_URL}/api/stores/${store.id}/promotions/${editingId}`
        : `${API_BASE_URL}/api/stores/${store.id}/promotions`;
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(payload) });
      if (res.ok) {
        showToast('success', editingId ? t('storePromotions:toast.updated') : t('storePromotions:toast.created'));
        cancelForm();
        await fetchPromotions();
      } else {
        const e = await res.json().catch(() => ({}));
        showToast('error', e.error || t('storePromotions:toast.saveFailed'));
      }
    } catch (e) {
      console.error(e);
      showToast('error', t('storePromotions:toast.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!store?.id || !confirmDelete.id) return;
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/stores/${store.id}/promotions/${confirmDelete.id}`,
        { method: 'DELETE', headers: headers() }
      );
      if (res.ok) {
        showToast('success', t('storePromotions:toast.deleted'));
        // Η προσφορά δεν υπάρχει πια: το συρτάρι της κλείνει.
        cancelForm();
        await fetchPromotions();
      } else {
        showToast('error', t('storePromotions:toast.deleteFailed'));
      }
    } catch (e) {
      showToast('error', t('storePromotions:toast.deleteFailed'));
    } finally {
      setConfirmDelete({ open: false, id: null });
    }
  };

  const toggleActive = async (promo: Promotion) => {
    if (!store?.id || !promo.id || togglingId !== null) return;
    const payload = { ...promo, is_active: !promo.is_active };
    setTogglingId(promo.id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/stores/${store.id}/promotions/${promo.id}`, {
        method: 'PUT', headers: headers(), body: JSON.stringify(payload)
      });
      if (res.ok) await fetchPromotions();
    } catch (e) {
      console.error(e);
    } finally {
      setTogglingId(null);
    }
  };

  const toggleDay = (dow: number) => {
    const current = form.days_of_week || [];
    const next = current.includes(dow) ? current.filter(d => d !== dow) : [...current, dow].sort();
    update({ days_of_week: next });
  };

  const toggleProduct = (id: number) => {
    const current = form.product_ids;
    update({ product_ids: current.includes(id) ? current.filter(x => x !== id) : [...current, id] });
  };

  const filteredProducts = useMemo(
    () => products.filter(p => matchesSearch(productSearch, p.name, p.category_name)),
    [products, productSearch],
  );

  const dayLabel = (dow: number) => t(`storePromotions:days.${DAY_KEYS[dow]}`);

  const formatTimeWindow = (p: Promotion) => {
    const parts: string[] = [];
    if (p.time_from && p.time_to) parts.push(`${String(p.time_from).slice(0, 5)}–${String(p.time_to).slice(0, 5)}`);
    if (p.days_of_week && p.days_of_week.length > 0 && p.days_of_week.length < 7) {
      parts.push(p.days_of_week.map(dayLabel).join(', '));
    }
    return parts.join(' · ');
  };

  // Ημερομηνίες ισχύος (η φόρμα τις είχε πάντα· η λίστα τώρα τις δείχνει δίπλα στο ωράριο).
  const formatDates = (p: Promotion) => {
    if (p.starts_at && p.ends_at) return `${fmtYmd(p.starts_at)} – ${fmtYmd(p.ends_at)}`;
    if (p.starts_at) return t('storePromotions:list.fromDate', { date: fmtYmd(p.starts_at) });
    if (p.ends_at) return t('storePromotions:list.untilDate', { date: fmtYmd(p.ends_at) });
    return '';
  };

  const describeDiscount = (p: Promotion) => {
    switch (p.promotion_type) {
      case 'percentage': return `-${p.discount_value}%`;
      case 'fixed_amount': return `-${formatMoney(Number(p.discount_value))}`;
      case 'buy_x_get_y': return `${p.buy_quantity}+${p.get_quantity}`;
      case 'bundle_price': return t('storePromotions:discount.bundle', { amount: formatMoney(Number(p.bundle_price)) });
      case 'order_fixed_amount': return t('storePromotions:discount.coupon', { amount: formatMoney(Number(p.discount_value)) });
      default: return '';
    }
  };

  const typeLabel = (type: PromotionType) => t(`storePromotions:types.${type}`);

  // «Ενεργή» της γραμμής: ένα πάτημα, όπως το κουμπί της παλιάς κάρτας (PUT με όλη την προσφορά).
  const activeSwitch = (p: Promotion) => (
    <button
      type="button"
      role="switch"
      aria-checked={p.is_active}
      aria-label={t('storePromotions:list.activeToggle', { name: p.name })}
      title={p.is_active ? t('storePromotions:list.active') : t('storePromotions:list.inactive')}
      disabled={togglingId !== null}
      onClick={() => toggleActive(p)}
      className={`relative shrink-0 w-11 h-6 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1 disabled:opacity-60 ${p.is_active ? 'bg-brand' : 'bg-surface-4 dark:bg-surface-3'}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${p.is_active ? 'translate-x-5' : ''}`} />
    </button>
  );

  const columns: Column<Promotion>[] = [
    {
      key: 'name',
      header: t('storePromotions:list.name'),
      width: 'minmax(180px, 2fr)',
      stack: 'title',
      render: p => (
        <>
          <span className="block font-medium">{p.name}</span>
          {p.description && <span className="block text-xs text-t3 truncate">{p.description}</span>}
        </>
      ),
      stackRender: p => p.name,
    },
    {
      key: 'promotion_type',
      header: t('storePromotions:list.type'),
      width: 'minmax(150px, 1.3fr)',
      render: p => <span className="text-t2">{typeLabel(p.promotion_type)}</span>,
      stackRender: p => typeLabel(p.promotion_type),
    },
    {
      key: 'discount',
      header: t('storePromotions:list.discount'),
      width: '128px',
      align: 'right',
      render: p => <span className="font-semibold">{describeDiscount(p)}</span>,
      stackRender: p => describeDiscount(p),
    },
    {
      key: 'when',
      header: t('storePromotions:list.when'),
      width: 'minmax(150px, 1.4fr)',
      hideBelow: 'medium',
      render: p => {
        const dates = formatDates(p);
        const window = formatTimeWindow(p);
        if (!dates && !window) return <span className="text-t3">{t('storePromotions:list.always')}</span>;
        return (
          <>
            {dates && <span className="block text-t2 tabular-nums">{dates}</span>}
            {window && <span className={`block tabular-nums ${dates ? 'text-xs text-t3' : 'text-t2'}`}>{window}</span>}
          </>
        );
      },
    },
    {
      key: 'products',
      header: t('storePromotions:list.products'),
      width: '128px',
      align: 'right',
      hideBelow: 'medium',
      render: p => (p.promotion_type === 'order_fixed_amount' && p.product_ids.length === 0
        ? <span className="text-t3">{t('storePromotions:list.wholeOrder')}</span>
        : <span className="text-t2">{t('storePromotions:list.productCount', { count: p.product_ids.length })}</span>),
    },
    {
      key: 'min_order_amount',
      header: t('storePromotions:list.minOrder'),
      width: '120px',
      align: 'right',
      hideBelow: 'medium',
      render: p => (p.min_order_amount && Number(p.min_order_amount) > 0
        ? <span className="text-t2">{formatMoney(Number(p.min_order_amount))}</span>
        : <span className="text-t4">—</span>),
    },
    {
      key: 'is_active',
      header: t('storePromotions:list.activeColumn'),
      width: '96px',
      align: 'center',
      stack: 'end',
      render: activeSwitch,
    },
  ];

  const activeCount = promotions.filter(p => p.is_active).length;
  const firstLoad = loading && promotions.length === 0;
  const errorFor = (field: ErrorField) => formError?.field === field;

  const numberInput = (
    id: string,
    value: number | null | undefined,
    onValue: (v: number | null) => void,
    opts: { step: number; min: number; max?: number; ref?: React.Ref<HTMLInputElement>; invalid?: boolean; parse?: (s: string) => number },
  ) => (
    <input
      id={id}
      ref={opts.ref}
      type="number"
      min={opts.min}
      max={opts.max}
      step={opts.step}
      value={value ?? ''}
      aria-invalid={opts.invalid || undefined}
      onChange={e => onValue(e.target.value === '' ? null : (opts.parse ?? parseFloat)(e.target.value))}
      className={`${INPUT_CLS} sm:max-w-[12rem] tabular-nums`}
    />
  );

  const renderTypeFields = () => {
    switch (form.promotion_type) {
      case 'percentage':
        return (
          <SheetField label={t('storePromotions:form.percentage')} htmlFor={`${uid}-value`}>
            {numberInput(`${uid}-value`, form.discount_value, v => update({ discount_value: v }), { min: 0, max: 100, step: 1, ref: discountRef, invalid: errorFor('discount') })}
          </SheetField>
        );
      case 'fixed_amount':
        return (
          <SheetField label={t('storePromotions:form.fixedAmount')} htmlFor={`${uid}-value`}>
            {numberInput(`${uid}-value`, form.discount_value, v => update({ discount_value: v }), { min: 0, step: 0.01, ref: discountRef, invalid: errorFor('discount') })}
          </SheetField>
        );
      case 'order_fixed_amount':
        return (
          <SheetField label={t('storePromotions:form.coupon')} htmlFor={`${uid}-value`} hint={t('storePromotions:form.couponHint')}>
            {numberInput(`${uid}-value`, form.discount_value, v => update({ discount_value: v }), { min: 0, step: 0.01, ref: discountRef, invalid: errorFor('discount') })}
          </SheetField>
        );
      case 'buy_x_get_y':
        return (
          <div>
            <div className="grid grid-cols-2 gap-4 max-w-md">
              <SheetField label={t('storePromotions:form.buy')} htmlFor={`${uid}-buy`}>
                {numberInput(`${uid}-buy`, form.buy_quantity, v => update({ buy_quantity: v }), { min: 1, step: 1, ref: buyRef, invalid: errorFor('quantities'), parse: s => parseInt(s, 10) })}
              </SheetField>
              <SheetField label={t('storePromotions:form.get')} htmlFor={`${uid}-get`}>
                {numberInput(`${uid}-get`, form.get_quantity, v => update({ get_quantity: v }), { min: 1, step: 1, invalid: errorFor('quantities'), parse: s => parseInt(s, 10) })}
              </SheetField>
            </div>
            <p className="xs-field-hint">{t('storePromotions:form.buyGetHint')}</p>
          </div>
        );
      case 'bundle_price':
        return (
          <SheetField label={t('storePromotions:form.bundle')} htmlFor={`${uid}-bundle`} hint={t('storePromotions:form.bundleHint')}>
            {numberInput(`${uid}-bundle`, form.bundle_price, v => update({ bundle_price: v }), { min: 0, step: 0.01, ref: bundleRef, invalid: errorFor('bundle') })}
          </SheetField>
        );
      default:
        return null;
    }
  };

  const productById = (id: number) => products.find(x => x.id === id);

  const renderProducts = () => (
    <div role="group" aria-labelledby={`${uid}-products-label`} data-testid="promotion-products">
      <div className="flex items-center justify-between gap-3 mb-2">
        <span id={`${uid}-products-label`} className="text-sm font-medium text-t2">
          {t('storePromotions:form.products', { count: form.product_ids.length })}
        </span>
        <button
          ref={pickerRef}
          type="button"
          className={ASIDE_BTN}
          aria-expanded={productPickerOpen}
          aria-controls={`${uid}-picker`}
          onClick={() => setProductPickerOpen(!productPickerOpen)}
        >
          {productPickerOpen ? t('storePromotions:form.closePicker') : t('storePromotions:form.openPicker')}
        </button>
      </div>

      {form.promotion_type === 'order_fixed_amount' && form.product_ids.length === 0 && !productPickerOpen && (
        <p className="text-xs text-t3">{t('storePromotions:form.wholeOrderHint')}</p>
      )}

      {form.product_ids.length > 0 && !productPickerOpen && (
        <ul className="flex flex-wrap gap-2" aria-label={t('storePromotions:form.selectedProducts')}>
          {form.product_ids.map(id => {
            const p = productById(id);
            if (!p) return null;
            return (
              <li key={id} className="inline-flex items-center gap-1 min-h-[32px] pl-3 pr-1 rounded-lg border border-line-strong bg-surface-2 text-sm text-t1">
                {p.name}
                <button
                  type="button"
                  onClick={() => toggleProduct(id)}
                  aria-label={t('storePromotions:form.removeProduct', { name: p.name })}
                  className="w-7 h-7 rounded-md text-t3 hover:text-t1 hover:bg-surface-3 inline-flex items-center justify-center"
                >
                  <X className="w-3.5 h-3.5" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {productPickerOpen && (
        <div id={`${uid}-picker`} className="rounded-xl border border-line-strong overflow-hidden" data-testid="promotion-product-picker">
          <div className="relative border-b border-line">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-t4" aria-hidden="true" />
            <input
              type="search"
              value={productSearch}
              onChange={e => setProductSearch(e.target.value)}
              placeholder={t('storePromotions:form.searchProduct')}
              aria-label={t('storePromotions:form.searchProduct')}
              className="w-full h-11 pl-10 pr-3 bg-surface-2 text-sm text-t1 placeholder-t4 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-ring/50"
            />
          </div>
          {filteredProducts.length === 0 ? (
            <p className="px-3.5 py-6 text-center text-sm text-t3">{t('storePromotions:form.noProducts')}</p>
          ) : (
            <ul className="max-h-72 overflow-y-auto divide-y divide-line">
              {filteredProducts.map(p => {
                const active = form.product_ids.includes(p.id);
                const meta = [p.category_name, formatMoney(p.price), t('storePromotions:form.maxDiscount', { value: p.max_discount_percent ?? 100 })]
                  .filter(Boolean).join(' · ');
                return (
                  <li key={p.id}>
                    <label className={`flex items-center gap-3 px-3.5 py-2.5 cursor-pointer transition-colors ${active ? 'bg-brand-soft' : 'hover:bg-surface-3/60'}`}>
                      <input type="checkbox" checked={active} onChange={() => toggleProduct(p.id)} className="w-4 h-4 shrink-0 accent-[hsl(var(--brand))]" />
                      <span className="min-w-0 flex-1">
                        <span className={`block text-sm truncate ${active ? 'font-semibold text-t1' : 'text-t1'}`}>{p.name}</span>
                        <span className="block text-xs text-t3 truncate tabular-nums">{meta}</span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );

  const renderForm = () => (
    <form id={formId} onSubmit={save} noValidate className="space-y-6" data-testid="promotion-form">
      <div className="xs-grid2">
        <SheetField label={t('storePromotions:form.name')} htmlFor={`${uid}-name`}>
          <input
            id={`${uid}-name`}
            ref={nameRef}
            type="text"
            value={form.name}
            autoComplete="off"
            onChange={e => update({ name: e.target.value })}
            placeholder={t('storePromotions:form.namePlaceholder')}
            aria-invalid={errorFor('name') || undefined}
            className={INPUT_CLS}
          />
        </SheetField>
        <SheetField label={t('storePromotions:form.description')} htmlFor={`${uid}-description`}>
          <input
            id={`${uid}-description`}
            type="text"
            value={form.description || ''}
            autoComplete="off"
            onChange={e => update({ description: e.target.value })}
            placeholder={t('storePromotions:form.descriptionPlaceholder')}
            className={INPUT_CLS}
          />
        </SheetField>
      </div>

      <fieldset>
        <legend className={LEGEND_CLS}>{t('storePromotions:form.type')}</legend>
        <div className="flex flex-wrap gap-2">
          {PROMOTION_TYPES.map(type => (
            <Choice
              key={type}
              type="radio"
              name={`${uid}-type`}
              checked={form.promotion_type === type}
              onChange={() => update({ promotion_type: type })}
              label={typeLabel(type)}
              testId={`promotion-type-${type}`}
            />
          ))}
        </div>
      </fieldset>

      {renderTypeFields()}

      <div className="xs-grid2">
        <fieldset>
          <legend className={LEGEND_CLS}>{t('storePromotions:form.dates')}</legend>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor={`${uid}-from`} className={SUB_LABEL_CLS}>{t('common:from')}</label>
              <input id={`${uid}-from`} type="date" value={form.starts_at || ''} onChange={e => update({ starts_at: e.target.value || null })} className={INPUT_CLS} />
            </div>
            <div>
              <label htmlFor={`${uid}-to`} className={SUB_LABEL_CLS}>{t('common:to')}</label>
              <input id={`${uid}-to`} type="date" value={form.ends_at || ''} onChange={e => update({ ends_at: e.target.value || null })} className={INPUT_CLS} />
            </div>
          </div>
        </fieldset>
        <fieldset>
          <legend className={LEGEND_CLS}>{t('storePromotions:form.hours')}</legend>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor={`${uid}-time-from`} className={SUB_LABEL_CLS}>{t('common:from')}</label>
              <input id={`${uid}-time-from`} type="time" value={form.time_from || ''} onChange={e => update({ time_from: e.target.value || null })} className={INPUT_CLS} />
            </div>
            <div>
              <label htmlFor={`${uid}-time-to`} className={SUB_LABEL_CLS}>{t('common:to')}</label>
              <input id={`${uid}-time-to`} type="time" value={form.time_to || ''} onChange={e => update({ time_to: e.target.value || null })} className={INPUT_CLS} />
            </div>
          </div>
        </fieldset>
      </div>

      <fieldset>
        <legend className={LEGEND_CLS}>{t('storePromotions:form.days')}</legend>
        <div className="flex flex-wrap gap-2">
          {DAY_KEYS.map((key, idx) => (
            <Choice
              key={key}
              type="checkbox"
              checked={!!form.days_of_week?.includes(idx)}
              onChange={() => toggleDay(idx)}
              label={dayLabel(idx)}
            />
          ))}
        </div>
      </fieldset>

      <SheetField label={t('storePromotions:form.minOrder')} htmlFor={`${uid}-min`}>
        <input
          id={`${uid}-min`}
          type="number"
          min={0}
          step={0.01}
          value={form.min_order_amount ?? 0}
          onChange={e => update({ min_order_amount: parseFloat(e.target.value) || 0 })}
          className={`${INPUT_CLS} sm:max-w-[12rem] tabular-nums`}
        />
      </SheetField>

      {renderProducts()}

      {conflictingProducts.length > 0 && (
        <Notice testId="promotion-conflicts">
          <p className="font-medium">{t('storePromotions:form.conflictTitle', { count: conflictingProducts.length })}</p>
          <ul className="mt-1 list-disc ml-5 space-y-0.5 text-xs">
            {conflictingProducts.map((p: ProductLite & { effective_percent: number }) => (
              <li key={p.id}>
                <strong>{p.name}</strong>: {t('storePromotions:form.conflictLine', { max: p.max_discount_percent, effective: p.effective_percent.toFixed(0) })}
              </li>
            ))}
          </ul>
        </Notice>
      )}

      <SwitchRow
        title={t('storePromotions:form.active')}
        subtitle={t('storePromotions:form.activeHint')}
        checked={form.is_active}
        onChange={value => update({ is_active: value })}
      />
    </form>
  );

  const panelFooter = (
    <>
      {formError && (
        <p role="alert" className="basis-full text-sm font-medium text-red-600 dark:text-red-400 text-right">{formError.message}</p>
      )}
      <button type="button" className={GHOST_BTN} onClick={requestClose} disabled={saving}>{t('storePromotions:form.cancel')}</button>
      <button type="submit" form={formId} className={PRIMARY_BTN} disabled={saving} aria-busy={saving || undefined}>
        {saving && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
        {editingId ? t('storePromotions:form.save') : t('storePromotions:form.create')}
      </button>
    </>
  );

  return (
    <ListPage
      eyebrow={t('settings:nav.groups.catalog')}
      title={t('settings:nav.promotions')}
      subtitle={t('storePromotions:subtitle')}
      testId="promotions-page"
    >
      <ListToolbar
        actions={(
          <button type="button" className={PRIMARY_BTN} onClick={startCreate} disabled={loading || loadState !== 'ready'}>
            <Plus className="w-4 h-4" aria-hidden="true" />
            {t('storePromotions:new')}
          </button>
        )}
        summary={loadState === 'ready' && !firstLoad
          ? `${t('storePromotions:count', { count: promotions.length })} · ${t('storePromotions:activeCount', { count: activeCount })}`
          : null}
      />

      {loadState !== 'ready' ? (
        <LoadFailed
          message={loadState === 'forbidden' ? t('storePromotions:forbidden') : t('storePromotions:loadFailed')}
          onRetry={loadAll}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={promotions}
          rowKey={p => p.id ?? p.name}
          onRowClick={startEdit}
          selectedKey={showForm ? editingId : null}
          loading={loading}
          stickyHeader
          rowClassName={p => (p.is_active ? undefined : 'opacity-60')}
          rowLabel={p => t('storePromotions:list.rowLabel', { name: p.name })}
          testId="promotions-table"
          empty={(
            <EmptyState
              icon={Tag}
              title={t('storePromotions:empty.title')}
              hint={t('storePromotions:empty.hint')}
            />
          )}
        />
      )}

      <DetailPanel
        open={showForm}
        onClose={requestClose}
        title={editingId ? t('storePromotions:form.editTitle') : t('storePromotions:form.newTitle')}
        subtitle={editingId ? initialForm.name : undefined}
        width="lg"
        footer={panelFooter}
        footerStart={editingId ? (
          <button type="button" className={DANGER_GHOST_BTN} onClick={() => setConfirmDelete({ open: true, id: editingId })} disabled={saving}>
            <Trash2 className="w-4 h-4" aria-hidden="true" />
            {t('storePromotions:delete.button')}
          </button>
        ) : undefined}
        testId="promotion-panel"
      >
        {showForm && renderForm()}
        {/* Τα παράθυρα επιβεβαίωσης μέσα στο συρτάρι: ζωγραφίζονται πάνω του, όχι από κάτω. */}
        <ConfirmModal
          isOpen={confirmDelete.open}
          title={t('storePromotions:delete.title')}
          message={t('storePromotions:delete.message')}
          confirmLabel={t('storePromotions:delete.confirm')}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete({ open: false, id: null })}
        />
        <ConfirmModal
          isOpen={confirmDiscard}
          title={t('storePromotions:discard.title')}
          message={t('storePromotions:discard.message')}
          confirmLabel={t('storePromotions:discard.confirm')}
          cancelLabel={t('storePromotions:discard.cancel')}
          variant="warning"
          onConfirm={cancelForm}
          onCancel={() => setConfirmDiscard(false)}
        />
      </DetailPanel>
    </ListPage>
  );
};

export default PromotionsManagement;
